import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './alert-dialog';

/**
 * Premium confirmation modal for delete actions
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onOpenChange - Callback when open state changes
 * @param {function} props.onConfirm - Callback when action is confirmed
 * @param {string} props.title - Dialog title (default: "Confirmer la suppression")
 * @param {string} props.description - Dialog description
 * @param {string} props.confirmText - Confirm button text (default: "Supprimer")
 * @param {string} props.cancelText - Cancel button text (default: "Annuler")
 * @param {boolean} props.loading - Whether action is in progress
 * @param {"danger" | "warning"} props.variant - Visual variant
 * @param {string} props.itemName - Name of the item being deleted (optional, for emphasis)
 */
export function ConfirmDeleteDialog({
    open,
    onOpenChange,
    onConfirm,
    title = "Confirmer la suppression",
    description = "Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.",
    confirmText = "Supprimer",
    cancelText = "Annuler",
    loading = false,
    variant = "danger",
    itemName
}) {
    const isDanger = variant === "danger";

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                    <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`
              flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
              ${isDanger
                                ? 'bg-red-100 dark:bg-red-900/30'
                                : 'bg-amber-100 dark:bg-amber-900/30'}
            `}>
                            {isDanger ? (
                                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                            ) : (
                                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            )}
                        </div>

                        {/* Text content */}
                        <div className="flex-1">
                            <AlertDialogTitle className="text-lg font-semibold">
                                {title}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="mt-2 text-sm text-muted-foreground">
                                {description}
                                {itemName && (
                                    <span className="block mt-2 px-3 py-2 bg-muted rounded-lg font-medium text-foreground">
                                        {itemName}
                                    </span>
                                )}
                            </AlertDialogDescription>
                        </div>
                    </div>
                </AlertDialogHeader>

                <AlertDialogFooter className="mt-6 gap-2 sm:gap-2">
                    <AlertDialogCancel
                        disabled={loading}
                        className="flex-1 sm:flex-none"
                    >
                        {cancelText}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            onConfirm();
                        }}
                        disabled={loading}
                        className={`
              flex-1 sm:flex-none gap-2
              ${isDanger
                                ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500'
                                : 'bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500'}
            `}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Suppression...
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-4 h-4" />
                                {confirmText}
                            </>
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

export default ConfirmDeleteDialog;
